import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { appRoutes, buildAiToolEditPath } from '../../app-routes';
import { PageHeader } from '../../components/common/PageHeader';
import {
  partnerApi,
  type ApiError,
  type ToolDefinitionInput,
  type ToolExecutionKind,
} from '../../lib/api';
import { LookupToolForm } from './LookupToolForm';
import {
  buildToolUpdatePatch,
  createHttpDraft,
  createLookupDraft,
  toolDefinitionToInput,
} from './lookup-tool-model';
import { ToolForm } from './ToolForm';
import { ToolTestPanel } from './ToolTestPanel';
import { ToolTypeChooser } from './ToolTypeChooser';
import styles from './LookupTool.module.css';

const TYPE_LABELS = {
  http: 'HTTP API',
  schema_lookup: 'Synced schema lookup',
} as const satisfies Record<ToolExecutionKind, string>;

export const ToolEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id || id === 'new';

  const [draft, setDraft] = useState<ToolDefinitionInput | null>(null);
  const [seededId, setSeededId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isChoosingType, setIsChoosingType] = useState(isNew);
  const [staleLookupConfiguration, setStaleLookupConfiguration] = useState(false);

  const existing = useQuery({
    queryKey: ['ai-tools', id],
    queryFn: () => partnerApi.getTool(id as string),
    enabled: !isNew && Boolean(id),
  });

  // Seed once per saved tool so background refetches cannot clobber in-progress edits.
  if (!isNew && existing.data && seededId !== id) {
    setSeededId(id ?? null);
    setDraft(toolDefinitionToInput(existing.data));
    setIsChoosingType(false);
    setIsFormValid(true);
  }

  const createMutation = useMutation({
    mutationFn: (input: ToolDefinitionInput) => partnerApi.createTool(input),
    onSuccess: created => {
      queryClient.invalidateQueries({ queryKey: ['ai-tools'] });
      navigate(buildAiToolEditPath(created.id));
      setSaveError(null);
    },
    onError: (error: ApiError) => setSaveError(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<ToolDefinitionInput>) => partnerApi.updateTool(id as string, patch),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ['ai-tools'] });
      queryClient.setQueryData(['ai-tools', id], updated);
      setDraft(toolDefinitionToInput(updated));
      setSaveError(null);
      setStaleLookupConfiguration(false);
    },
    onError: (error: ApiError) => {
      const stringErrors = error.response?.data?.errors?.filter(
        (detail): detail is string => typeof detail === 'string',
      );
      const details = error.response?.data?.details ?? stringErrors;
      const detailText = details?.length ? `\n${details.join('\n')}` : '';
      setSaveError(`${error.message}${detailText}`);
    },
  });

  const savedTool = existing.data;
  const patch =
    savedTool && draft
      ? buildToolUpdatePatch({
          saved: savedTool,
          draft,
          forceLookupConfig: staleLookupConfiguration,
        })
      : {};
  const canDeactivateStaleLookup =
    draft?.execution_kind === 'schema_lookup' &&
    draft.status === 'inactive' &&
    Object.keys(patch).length === 1 &&
    patch.status === 'inactive';

  const handleSave = () => {
    if (!draft) return;
    setSaveError(null);
    if (isNew) {
      createMutation.mutate(draft);
      return;
    }
    if (Object.keys(patch).length === 0) {
      return;
    }
    updateMutation.mutate(patch);
  };

  const handleTypeSelect = (kind: ToolExecutionKind) => {
    setDraft(
      kind === 'http' ? createHttpDraft(draft ?? undefined) : createLookupDraft(draft ?? undefined),
    );
    setIsChoosingType(false);
    setIsFormValid(false);
    setStaleLookupConfiguration(false);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveDisabled =
    !draft ||
    isSaving ||
    (!isFormValid && !canDeactivateStaleLookup) ||
    (!isNew && !Object.keys(patch).length);
  const headerTitle = isNew ? 'New tool' : savedTool?.name || 'Edit tool';
  const headerDescription = isNew
    ? 'Choose how this tool runs, then configure what the agent can do.'
    : savedTool?.description || '';
  let saveButtonLabel = 'Save';
  if (isNew) saveButtonLabel = 'Create tool';
  if (isSaving) saveButtonLabel = 'Saving…';

  return (
    <main className={styles.page}>
      <PageHeader
        title={headerTitle}
        description={headerDescription}
        right={
          <nav className={styles.headerActions} aria-label="Tool actions">
            <button type="button" onClick={() => navigate(appRoutes.AI_TOOLS)}>
              Back
            </button>
            {!isChoosingType && draft && (
              <button
                type="button"
                onClick={handleSave}
                disabled={saveDisabled}
                title={
                  !isFormValid && !canDeactivateStaleLookup ? 'Fix form errors before saving' : undefined
                }
                className={styles.headerSaveButton}>
                {saveButtonLabel}
              </button>
            )}
          </nav>
        }
      />

      {saveError && <aside className={styles.saveError}>{saveError}</aside>}

      {isChoosingType ? (
        <ToolTypeChooser onSelect={handleTypeSelect} />
      ) : (
        draft && (
          <>
            <section className={styles.typeBar}>
              <div>
                <span>Execution type</span>
                <strong>{TYPE_LABELS[draft.execution_kind]}</strong>
              </div>
              {isNew && (
                <button type="button" onClick={() => setIsChoosingType(true)}>
                  Change type
                </button>
              )}
            </section>

            <section className={styles.editorLayout}>
              <article className={styles.formCard}>
                {draft.execution_kind === 'http' ? (
                  <ToolForm
                    draft={draft}
                    onChange={setDraft}
                    isEditing={!isNew}
                    onValidityChange={setIsFormValid}
                    canonicalName={savedTool?.name}
                  />
                ) : (
                  <LookupToolForm
                    draft={draft}
                    onChange={setDraft}
                    isEditing={!isNew}
                    canonicalName={savedTool?.name}
                    storedConfig={
                      savedTool?.execution_kind === 'schema_lookup' ? savedTool.lookup_config : undefined
                    }
                    onValidityChange={setIsFormValid}
                    onStaleConfigurationChange={setStaleLookupConfiguration}
                  />
                )}
              </article>

              {!isNew && savedTool && (
                <aside className={styles.testPanel}>
                  <ToolTestPanel tool={savedTool} />
                </aside>
              )}
            </section>
          </>
        )
      )}
    </main>
  );
};
